import { useQueryClient } from "@tanstack/react-query";
import { Link, type LinkProps, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  BookOpen,
  BuildingComplexPlus,
  FileText,
  Handshake,
  Info,
  LogOut,
  type LucideIcon,
  MapPin,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Search,
  Settings,
  ShoppingCart,
  Sun,
  Warehouse,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  keys,
  signOut,
  useBorrowing,
  useBrewSystems,
  useBrews,
  useInvoices,
  useItems,
  useLocations,
  useRecipeDrafts,
  useRecipes,
  useSession,
  useStockImports,
} from "../api";
import { useArrivalGlow } from "../arrival";
import { showOnHand } from "../format";
import { useLayout, useThemeEffect } from "../layout/LayoutProvider";
import { useShowLocation } from "../pages/Inventory";
import { CURRENT_RELEASE } from "../releaseNotes";
import { type Hit, KINDS, type PageEntry, searchEverything } from "../search";
import { AboutDialog } from "./About";
import { BrewPills } from "./BrewPills";
import { useMedia } from "./DocumentViewer";
import { NotificationBell } from "./NotificationBell";

interface NavPage {
  to: LinkProps["to"] & string;
  label: string;
  icon: LucideIcon;
}

/** A page with the pages under it. Its pages show under it while you are anywhere in it. */
interface NavSection extends NavPage {
  pages: NavPage[];
  /** Further paths that belong to it: an item's page is Inventory's. */
  also?: string[];
}

/**
 * The sidebar's sections (Tyler, 2026-09-30), Settings last below the divider. Kept to a few parents so
 * the sidebar stays short as slices add pages. Locations sit with Inventory, and History is reached from
 * Settings rather than the sidebar (Tyler, 2026-10-01). Production holds the brewing: its own page,
 * the tank schedule over the cellar, with Recipes under it and the Recipe builder reached from Recipes
 * (Tyler, 2026-10-03). The Calendar and Brew day left it (Scott's Jam, 2026-10-08: "Calendar has been
 * replaced by the default production tab"): a brew opens from its bar on Production, and To order,
 * the Calendar's list of what to buy, sits under Inventory beside Invoices (Tyler, 2026-10-19).
 */
export const SECTIONS: NavSection[] = [
  {
    to: "/",
    label: "Inventory",
    icon: Warehouse,
    also: ["/items", "/count"],
    pages: [
      { to: "/invoices", label: "Invoices", icon: FileText },
      { to: "/to-order", label: "To order", icon: ShoppingCart },
      { to: "/borrowing", label: "Borrowing", icon: Handshake },
      { to: "/locations", label: "Locations", icon: MapPin },
    ],
  },
  {
    to: "/production",
    label: "Production",
    icon: BuildingComplexPlus,
    pages: [{ to: "/recipes", label: "Recipes", icon: BookOpen }],
    // A brew's own page, opened from its bar on the tank schedule.
    also: ["/brews"],
  },
];

export const SETTINGS: NavSection = {
  to: "/settings",
  label: "Settings",
  icon: Settings,
  pages: [],
  also: ["/movements"],
};

const within = (path: string, prefix: string) =>
  prefix !== "/" && (path === prefix || path.startsWith(`${prefix}/`));

export function inSection(section: NavSection, path: string): boolean {
  if (path === section.to) return true;
  return [section.to, ...section.pages.map((p) => p.to), ...(section.also ?? [])].some((prefix) =>
    within(path, prefix),
  );
}

/**
 * Lucide's panel-left-close, drawn here so its arrow can turn: collapsed, it is turned half round to
 * point the other way, exactly panel-left-open (Tyler, 2026-10-02).
 */
function MenuToggleIcon() {
  return (
    <svg
      width={19}
      height={19}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
      <path className="menu-toggle-arrow" d="m16 15-3-3 3-3" />
    </svg>
  );
}

/**
 * A section: its own link, and its pages under it while you are in it. With the menu collapsed to
 * icons those pages are icons under it, and another section's open beside its icon on hover or focus,
 * so nothing needs a hover on a touch screen: tapping a section opens it and shows its pages. The phone
 * drawer shows every section's pages.
 */
function NavGroup({ section, path }: { section: NavSection; path: string }) {
  const active = inSection(section, path);
  const Icon = section.icon;
  return (
    <div className={`nav-section ${active ? "active" : ""}`}>
      <Link
        to={section.to}
        activeOptions={{ exact: true }}
        title={section.label}
        // A section with no pages of its own in the menu stands for the pages reached from it: History
        // is Settings' (Tyler, 2026-10-01).
        className={active && section.pages.length === 0 ? "current" : undefined}
      >
        <Icon size={19} />
        <span>{section.label}</span>
      </Link>
      {section.pages.length ? (
        <div className="nav-pages">
          <span className="nav-flyout-title">{section.label}</span>
          {section.pages.map(({ to, label, icon: PageIcon }) => (
            <Link key={to} to={to} title={label}>
              <PageIcon size={17} />
              <span>{label}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Further words a sidebar page is looked for by. */
const PAGE_KEYWORDS: Record<string, string[]> = {
  "/": ["stock", "items"],
  "/borrowing": ["loans", "lent", "borrowed"],
  "/to-order": ["order", "buy", "shopping list", "shortfall"],
  "/production": [
    "cellar",
    "tanks",
    "fermenters",
    "brite tanks",
    "schedule",
    "calendar",
    "planned brews",
    "brew day",
  ],
  "/settings": ["preferences"],
};

/**
 * What the header's search finds by name besides the sidebar's pages: the pages reached from them,
 * and each part of Settings, which opens scrolled to it (Tyler, 2026-10-03: "Even settings").
 */
const MORE_PAGES: PageEntry[] = [
  {
    label: "Recipe builder",
    href: "/recipes/builder",
    detail: "Recipes",
    keywords: ["new recipe"],
  },
  { label: "History", href: "/movements", detail: "Settings", keywords: ["movements"] },
  {
    label: "Count sheet",
    href: "/count",
    detail: "Inventory",
    keywords: ["count", "stock take", "inventory count"],
  },
  { label: "Notifications", href: "/notifications" },
  {
    label: "Where deliveries go",
    href: "/locations#where-deliveries-go",
    detail: "Locations",
    keywords: ["default location"],
  },
  {
    label: "Layouts on this device",
    href: "/settings#layouts",
    detail: "Settings",
    keywords: ["layout"],
  },
  {
    label: "Display",
    href: "/settings#display",
    detail: "Settings",
    keywords: ["theme", "dark mode", "light mode", "malt color", "lovibond", "srm", "ebc", "units"],
  },
  {
    label: "Equipment",
    href: "/settings#equipment",
    detail: "Settings",
    keywords: ["brewhouse", "cellar", "brewery layout"],
  },
  {
    label: "Brew systems",
    href: "/settings#brew-systems",
    detail: "Settings",
    keywords: ["equipment", "brewhouse"],
  },
  {
    label: "Cellar tanks",
    href: "/settings#cellar",
    detail: "Settings",
    keywords: ["tanks", "fermenters", "brite tanks", "yeast brinks", "brewery layout"],
  },
  {
    label: "Maker Aliases",
    href: "/settings#makers",
    detail: "Settings",
    keywords: ["makers", "brands"],
  },
  {
    label: "Import from Ollie",
    href: "/settings#import-from-ollie",
    detail: "Settings",
    keywords: ["ollie"],
  },
  { label: "API", href: "/settings#api", detail: "Settings" },
  {
    label: "Empty this brewery",
    href: "/settings#empty-this-brewery",
    detail: "Settings",
    keywords: ["reset"],
  },
];

export const SEARCH_PAGES: PageEntry[] = [
  ...[...SECTIONS, SETTINGS]
    .flatMap((s) => [s, ...s.pages])
    .map((p) => ({ label: p.label, href: p.to, keywords: PAGE_KEYWORDS[p.to] })),
  ...MORE_PAGES,
];

/**
 * The search at the top of every page finds anything in the brewery (Tyler, 2026-10-03: "full omni
 * search"), grouped by kind, and each result opens its own page. Items are on hand on every page; the
 * rest of the brewery is loaded the first time someone types here, and kept fresh after.
 */
export function EverythingSearch() {
  const { layout } = useLayout();
  const navigate = useNavigate();
  const showLocation = useShowLocation();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const listId = useId();
  const optionId = (n: number) => `${listId}-${n}`;
  const when = { enabled: wanted };
  const { data: items } = useItems();
  const { data: recipes } = useRecipes(when);
  const { data: brews } = useBrews(when);
  const { data: invoices } = useInvoices(undefined, when);
  const { data: locations } = useLocations(when);
  const { data: balances } = useBorrowing(when);
  const { data: brewSystems } = useBrewSystems(when);
  const { data: drafts } = useRecipeDrafts(when);
  const { data: ollieReports } = useStockImports(when);
  const hits = useMemo(
    () =>
      searchEverything(q, {
        pages: SEARCH_PAGES,
        items,
        recipes,
        brews,
        invoices,
        locations,
        breweries: balances ? [...new Set(balances.map((b) => b.counterparty))] : undefined,
        brewSystems,
        drafts,
        ollieReports,
      }),
    [q, items, recipes, brews, invoices, locations, balances, brewSystems, drafts, ollieReports],
  );
  const byId = useMemo(() => new Map((items ?? []).map((i) => [i.id, i])), [items]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const go = (hit: Hit) => {
    setQ("");
    setOpen(false);
    if (hit.locationId) showLocation(hit.locationId);
    navigate({ href: hit.href });
  };
  const shown = open && q.trim() !== "";
  // The arrow keys move through what it found; the one marked stays in sight as the list scrolls.
  useEffect(() => {
    if (shown)
      document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [shown, active, listId]);
  // What it found, by kind, in the order the list shows them, each with its place in that list.
  const groups: { kind: Hit["kind"]; hits: { hit: Hit; n: number }[] }[] = [];
  hits.forEach((hit, n) => {
    const last = groups.at(-1);
    if (last?.kind === hit.kind) last.hits.push({ hit, n });
    else groups.push({ kind: hit.kind, hits: [{ hit, n }] });
  });

  return (
    <div className="header-search" ref={box}>
      <Search size={16} aria-hidden />
      {/* A combobox with a list of what it finds (the re-critique, 2026-10-06: a screen reader heard a
          plain search box, and nothing of the results under it). */}
      <input
        type="search"
        placeholder="Search"
        aria-label="Search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={shown}
        aria-controls={listId}
        aria-activedescendant={shown && hits[active] ? optionId(active) : undefined}
        value={q}
        onFocus={() => {
          setWanted(true);
          setOpen(true);
        }}
        onChange={(e) => {
          setWanted(true);
          setQ(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, hits.length - 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          }
          const hit = hits[active];
          if (e.key === "Enter" && hit) go(hit);
          // Escape closes what it found, and a second leaves the field (on a phone, folding it away).
          if (e.key === "Escape") {
            if (shown) setOpen(false);
            else e.currentTarget.blur();
          }
        }}
      />
      <div className="sr-only" role="status">
        {shown ? (hits.length ? `${hits.length} found` : "Nothing found") : ""}
      </div>
      {shown ? (
        <div className="search-results">
          {hits.length === 0 ? <p className="search-none">Nothing found</p> : null}
          <div role="listbox" id={listId} aria-label="What search found">
            {groups.map((g) => {
              const label = KINDS.find((k) => k.kind === g.kind)?.label;
              return (
                // biome-ignore lint/a11y/useSemanticElements: a group of a listbox's options, not of form fields
                <div key={g.kind} role="group" aria-label={label} className="search-group">
                  <div className="search-kind" aria-hidden>
                    {label}
                  </div>
                  {g.hits.map(({ hit: h, n }) => {
                    const item =
                      h.kind === "item" ? byId.get(h.key.slice("item:".length)) : undefined;
                    return (
                      <a
                        key={h.key}
                        id={optionId(n)}
                        role="option"
                        aria-selected={n === active}
                        tabIndex={-1}
                        href={h.href}
                        data-active={n === active}
                        onMouseEnter={() => setActive(n)}
                        onClick={(e) => {
                          e.preventDefault();
                          go(h);
                        }}
                      >
                        <span className="search-title">
                          {h.title}
                          {h.detail ? <span className="faint"> · {h.detail}</span> : null}
                        </span>
                        {item ? (
                          <span className="muted num">
                            {showOnHand(item, layout.inventory.quantityMode)}
                          </span>
                        ) : null}
                      </a>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ThemeButton() {
  const { layout, update } = useLayout();
  const order = ["auto", "light", "dark"] as const;
  const theme = layout.preferences.theme;
  const next = order[(order.indexOf(theme) + 1) % order.length] as (typeof order)[number];
  const Icon = theme === "auto" ? Monitor : theme === "light" ? Sun : Moon;
  return (
    <button
      type="button"
      className="icon-button"
      title={`Theme: ${theme}. Switch to ${next}.`}
      aria-label={`Theme: ${theme}`}
      onClick={() => update((l) => ({ ...l, preferences: { ...l.preferences, theme: next } }))}
    >
      <Icon size={18} />
    </button>
  );
}

/**
 * A new page, said as one: the keyboard goes on from the top of the page rather than the link left
 * behind, and a screen reader hears the page's name (the re-critique, 2026-10-07: moving between pages
 * left focus on the sidebar link and said nothing). The name is said once the page has had a moment to
 * load and name itself. Not on first opening, and not where the page has put the focus itself.
 */
function useArrival(pathname: string): string {
  const [said, setSaid] = useState("");
  const first = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const main = document.getElementById("main");
    if (main && !main.contains(document.activeElement)) main.focus({ preventScroll: true });
    setSaid("");
    const t = setTimeout(() => setSaid(document.title.replace(/ · BreweryLogistics$/, "")), 500);
    return () => clearTimeout(t);
  }, [pathname]);
  return said;
}

export function Shell() {
  useThemeEffect();
  const { layout, update } = useLayout();
  const [drawer, setDrawer] = useState(false);
  const [about, setAbout] = useState(false);
  const phone = useMedia("(max-width: 760px)");
  const toggle = useRef<HTMLButtonElement>(null);
  const side = useRef<HTMLElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // A part of a page opened by name, as search opens one of Settings', glows as it arrives.
  const hash = useRouterState({ select: (s) => s.location.hash });
  const visit = useRouterState({ select: (s) => s.location.state.__TSR_key });
  useArrivalGlow(hash, visit);
  // Close the phone drawer whenever the page changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger
  useEffect(() => setDrawer(false), [pathname]);
  const arrived = useArrival(pathname);
  // The open drawer takes the focus, and Escape closes it, back to the button that opened it (the
  // audit, 2026-10-06: it was tabbed through while closed, and Escape did nothing).
  useEffect(() => {
    if (!drawer) return;
    side.current?.querySelector<HTMLElement>("a, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setDrawer(false);
      toggle.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer]);
  const collapsed = layout.preferences.sidebarCollapsed;
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  return (
    <div className={`shell ${collapsed ? "sidebar-collapsed" : ""}`}>
      {/* The first stop for a keyboard: past the header and the menu, to the page. */}
      <a className="skip-link" href="#main">
        Skip to the page
      </a>
      <header className="header">
        <button
          ref={toggle}
          type="button"
          className="icon-button menu-toggle"
          aria-label="Menu"
          aria-expanded={drawer}
          onClick={() => setDrawer((d) => !d)}
        >
          <MenuIcon size={20} />
        </button>
        <Link to="/" className="brand" aria-label="BreweryLogistics, Inventory">
          <img src="/logo-64.png" alt="" />
          <span>BreweryLogistics</span>
        </Link>
        <BrewPills />
        <EverythingSearch />
        <ThemeButton />
        <NotificationBell />
      </header>
      {/* On a phone the menu is a drawer, out of reach of the keyboard and screen readers while shut. */}
      <aside ref={side} className={`sidebar ${drawer ? "open" : ""}`} inert={phone && !drawer}>
        <nav aria-label="Main">
          {SECTIONS.map((section) => (
            <NavGroup key={section.to} section={section} path={pathname} />
          ))}
          <div className="nav-divider" />
          <NavGroup section={SETTINGS} path={pathname} />
          <button
            type="button"
            className="nav-button collapse-toggle"
            title={collapsed ? "Expand menu" : "Collapse menu"}
            aria-pressed={collapsed}
            onClick={() =>
              update((l) => ({
                ...l,
                preferences: { ...l.preferences, sidebarCollapsed: !collapsed },
              }))
            }
          >
            <MenuToggleIcon />
            <span>Collapse menu</span>
          </button>
        </nav>
        <div className="sidebar-bottom">
          <button
            type="button"
            className="nav-button"
            title="About"
            onClick={() => {
              setDrawer(false);
              setAbout(true);
            }}
          >
            <Info size={19} />
            <span>About</span>
          </button>
          {session?.required ? (
            <button
              type="button"
              className="nav-button"
              title="Sign out"
              onClick={async () => {
                const state = await signOut();
                // Flip the session first: clearing the whole cache would detach the observer that
                // decides whether to show the sign-in page. Then drop everything else it held.
                queryClient.setQueryData(keys.session, state);
                queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== keys.session[0] });
              }}
            >
              <LogOut size={19} />
              <span>Sign out</span>
            </button>
          ) : null}
          <div className="sidebar-footer">White Oak Brewing · {CURRENT_RELEASE.version}</div>
        </div>
      </aside>
      <AboutDialog open={about} onClose={() => setAbout(false)} />
      <button
        type="button"
        className={`scrim ${drawer ? "open" : ""}`}
        aria-label="Close menu"
        tabIndex={-1}
        onClick={() => setDrawer(false)}
      />
      <main className="main" id="main" tabIndex={-1}>
        <Outlet />
      </main>
      <div className="sr-only" role="status">
        {arrived}
      </div>
    </div>
  );
}
